import { defineStore } from 'pinia'
import { ref } from 'vue'
import type { BountyExtensionWindowDto } from '@roadfootage/api-client'
import { useApi } from '../../composables/api'
import { readApiErrorMessage } from '../../utils/api-error'

const LOAD_ERROR = 'Could not load this bounty. Please try again.'
const EXTEND_ERROR = 'Could not extend this bounty. Please try again.'

/** Staff "Extend offer" flow for one bounty: read the allowed window, then move the expiry. */
export const useBountyExtensionStore = defineStore('bounty-extension', () => {
  // Silent: the dialog shows the server's text itself (a 409 explains what changed).
  const { adminBountiesApi } = useApi({ notifyOnError: false })

  const bountyId = ref<string | null>(null)
  const offerWindow = ref<BountyExtensionWindowDto | null>(null)
  const loading = ref(false)
  const submitting = ref(false)
  const error = ref<string | null>(null)

  function reset() {
    bountyId.value = null
    offerWindow.value = null
    loading.value = false
    submitting.value = false
    error.value = null
  }

  async function open(id: string) {
    reset()
    bountyId.value = id
    loading.value = true
    try {
      const response = await adminBountiesApi.adminBountiesGetExtensionWindowById({ id })
      // Ignore a late answer for a dialog that was closed or reopened for another bounty.
      if (bountyId.value !== id) return
      offerWindow.value = response
    } catch (e) {
      console.error('Failed to load the bounty extension window', e)
      const message = await readApiErrorMessage(e, LOAD_ERROR)
      if (bountyId.value === id) error.value = message
    } finally {
      if (bountyId.value === id) loading.value = false
    }
  }

  /** True when the expiry moved. On false, `error` holds the server's text. */
  async function extend(id: string, newExpiresAt: Date, notifyPoster: boolean): Promise<boolean> {
    if (submitting.value) return false
    submitting.value = true
    error.value = null
    try {
      await adminBountiesApi.adminBountiesExtendById({
        id,
        extendBountyRequest: { newExpiresAt, notifyPoster }
      })
      return true
    } catch (e) {
      console.error('Failed to extend the bounty', e)
      const message = await readApiErrorMessage(e, EXTEND_ERROR)
      if (bountyId.value === id) error.value = message
      return false
    } finally {
      submitting.value = false
    }
  }

  return { bountyId, offerWindow, loading, submitting, error, open, extend, reset }
})
